import { EnvironmentProviders, inject, makeEnvironmentProviders } from '@angular/core';

import { OrganizationsRepository } from './domain/interfaces/organizations.interface';
import { ORGANIZATIONS_CONFIG } from './infrastructure/organizations.config';
import { OrganizationsHttpRepository } from './infrastructure/repositories/organizations.http.repository';
import { OrganizationsMockRepository } from './infrastructure/repositories/organizations.mock.repository';
import { OrgWheelFacade } from './application/org-wheel.facade';
import { OrgEngagementFacade } from './application/org-engagement.facade';
import { OrgHealthFacade } from './application/org-health.facade';
import { OrgCounselingFacade } from './application/org-counseling.facade';
import { OrgGuardianFacade } from './application/org-guardian.facade';

/**
 * provideOrganizationsData — the ROOT-wide repository binding (config-driven
 * mock/http selection).
 *
 * Mounted at the TRUE app root in `app.config.ts`, NOT inside a route's
 * providers, for exactly the reason `provideSubscriptionData` documents:
 * `OrgContextStore` is a plain `@Service()` (root-scoped), so it is constructed
 * against the real root injector and a route-level injector is invisible to it.
 * Registering the repository only at route level would give NG0201 for any
 * root-scoped consumer that forces the store to construct — and the coverage
 * badge, the Home hub and the commerce gates all do.
 *
 * The binding lives here rather than on the abstract class so
 * `domain/interfaces/organizations.interface.ts` never imports infrastructure
 * (the layer-direction rule `check:architecture` enforces).
 */
export function provideOrganizationsData(): EnvironmentProviders {
  return makeEnvironmentProviders([
    OrganizationsMockRepository,
    OrganizationsHttpRepository,
    {
      provide: OrganizationsRepository,
      useFactory: () =>
        inject(ORGANIZATIONS_CONFIG).useMock
          ? inject(OrganizationsMockRepository)
          : inject(OrganizationsHttpRepository),
    },
  ]);
}

/**
 * provideOrganizations — the ROUTE-scoped facades for the `/org` surfaces
 * (counseling, guardian, gifted programmes).
 *
 * Each owns per-visit state (the picked slot, the open digest, a draft note), so
 * they live and die with the visit. `OrgContextStore` is deliberately NOT here —
 * it is root-scoped, because membership gates UI far outside these routes.
 */
export function provideOrganizations(): EnvironmentProviders {
  return makeEnvironmentProviders([OrgCounselingFacade, OrgGuardianFacade]);
}

/**
 * Route-scoped state for the B2B Home Hub tabs. Supplied by the Home route
 * alongside its own providers — harmless for a non-member, who never mounts the
 * components that inject them, so they are always provided rather than
 * conditionally (the same call `HOME_PROVIDERS` already makes for its stores).
 */
export function provideOrgHomeHub(): EnvironmentProviders {
  return makeEnvironmentProviders([OrgWheelFacade, OrgEngagementFacade, OrgHealthFacade]);
}
